import * as React from "react"

import { cn } from "../lib/utils"

type PageHeaderProps = {
  title: React.ReactNode
  subtitle?: React.ReactNode
  /** Alias for `subtitle` — accepted for convenience. */
  description?: React.ReactNode
  /** Optional small pill rendered above the title (landing-style eyebrow). */
  eyebrow?: React.ReactNode
  actions?: React.ReactNode
  /** Alias for `actions` — allows <PageHeader>...</PageHeader> children syntax. */
  children?: React.ReactNode
  className?: string
}

export function PageHeader({ title, subtitle, description, eyebrow, actions, children, className }: PageHeaderProps) {
  const sub = subtitle ?? description
  const act = actions ?? children
  return (
    <div
      data-slot="page-header"
      className={cn(
        // Stack vertically on phones AND tablets (≤ md). At lg (≥ 1024 px)
        // there's enough horizontal room for title + actions side-by-side
        // without the title being truncated to "Das…".
        "mb-8 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between",
        className,
      )}
    >
      <div className="min-w-0">
        {eyebrow && <div className="ly-eyebrow-pill mb-3">{eyebrow}</div>}
        <h1 className="truncate text-2xl font-semibold tracking-[-0.02em] sm:text-[1.85rem] lg:text-[2rem]">
          {title}
        </h1>
        {sub && (
          <p className="mt-2 max-w-2xl text-[0.95rem] leading-relaxed text-muted-foreground">{sub}</p>
        )}
      </div>
      {act && (
        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto lg:shrink-0">{act}</div>
      )}
    </div>
  )
}
